/**
 * ThemePreviewPanel
 *
 * Renders a mini showcase of common MUI Joy components so the admin
 * can see the effect of their colour choices in real-time.
 * The panel is purely presentational — no state, no side effects.
 */
import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Input,
  LinearProgress,
  Option,
  Select,
  Stack,
  Switch,
  Typography,
} from "@mui/joy";
import {
  CheckCircle as CheckIcon,
  Info as InfoIcon,
  Warning as WarnIcon,
} from "@mui/icons-material";

const ThemePreviewPanel: React.FC = () => (
  <Stack spacing={2}>
    {/* Typography */}
    <Box>
      <Typography level="h3" sx={{ mb: 0.5 }}>
        Heading
      </Typography>
      <Typography level="title-md">Title medium</Typography>
      <Typography level="body-sm" color="neutral">
        Body text — secondary neutral
      </Typography>
    </Box>

    <Divider />

    {/* Buttons */}
    <Box>
      <Typography level="title-sm" sx={{ mb: 1, fontWeight: 600 }}>
        Buttons
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Button size="sm" variant="solid">Solid</Button>
        <Button size="sm" variant="outlined">Outlined</Button>
        <Button size="sm" variant="soft">Soft</Button>
        <Button size="sm" variant="plain">Plain</Button>
      </Stack>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
        <Button size="sm" color="success">Success</Button>
        <Button size="sm" color="warning">Warning</Button>
        <Button size="sm" color="danger">Danger</Button>
        <Button size="sm" color="neutral">Neutral</Button>
      </Stack>
    </Box>

    <Divider />

    {/* Chips */}
    <Box>
      <Typography level="title-sm" sx={{ mb: 1, fontWeight: 600 }}>
        Chips
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Chip size="sm" variant="soft" color="primary">Primary</Chip>
        <Chip size="sm" variant="soft" color="success">Success</Chip>
        <Chip size="sm" variant="soft" color="warning">Warning</Chip>
        <Chip size="sm" variant="soft" color="danger">Danger</Chip>
        <Chip size="sm" variant="soft" color="neutral">Neutral</Chip>
      </Stack>
    </Box>

    <Divider />

    {/* Alerts */}
    <Box>
      <Typography level="title-sm" sx={{ mb: 1, fontWeight: 600 }}>
        Alerts
      </Typography>
      <Stack spacing={1}>
        <Alert
          variant="soft"
          color="primary"
          startDecorator={<InfoIcon />}
          size="sm"
        >
          Informational alert
        </Alert>
        <Alert
          variant="soft"
          color="success"
          startDecorator={<CheckIcon />}
          size="sm"
        >
          Success message
        </Alert>
        <Alert
          variant="soft"
          color="warning"
          startDecorator={<WarnIcon />}
          size="sm"
        >
          Warning message
        </Alert>
        <Alert variant="soft" color="danger" size="sm">
          Error / danger state
        </Alert>
      </Stack>
    </Box>

    <Divider />

    {/* Form inputs */}
    <Box>
      <Typography level="title-sm" sx={{ mb: 1, fontWeight: 600 }}>
        Form Controls
      </Typography>
      <Stack spacing={1}>
        <Input size="sm" placeholder="Text input…" />
        <Select
          size="sm"
          placeholder="Select an option"
          slotProps={{ listbox: { disablePortal: true } }}
        >
          <Option value="a">Option A</Option>
          <Option value="b">Option B</Option>
        </Select>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Switch size="sm" defaultChecked />
          <Typography level="body-sm">Toggle switch</Typography>
        </Box>
      </Stack>
    </Box>

    <Divider />

    {/* Progress */}
    <Box>
      <Typography level="title-sm" sx={{ mb: 1, fontWeight: 600 }}>
        Progress
      </Typography>
      <Stack spacing={1}>
        <LinearProgress value={60} determinate />
        <Box sx={{ display: "flex", gap: 2 }}>
          <CircularProgress size="sm" value={75} determinate />
          <CircularProgress size="sm" color="success" value={40} determinate />
          <CircularProgress size="sm" color="warning" value={85} determinate />
        </Box>
      </Stack>
    </Box>

    <Divider />

    {/* Card */}
    <Card variant="outlined" size="sm">
      <CardContent>
        <Typography level="title-sm">Card component</Typography>
        <Typography level="body-xs" color="neutral" sx={{ mt: 0.5 }}>
          Cards use the surface background colour and divider border.
        </Typography>
        <Button size="sm" variant="soft" sx={{ mt: 1 }}>
          Card action
        </Button>
      </CardContent>
    </Card>
  </Stack>
);

export default ThemePreviewPanel;
